import { useState } from 'react';
import { Alert, Anchor, Button, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toUserMessage } from '@/lib/api';
import { AuthLayout } from '@/features/auth/AuthLayout';
import { unsubscribe } from './api';

/**
 * Отписка по ссылке «Отписаться» из письма рассылки (docs/PRIVACY.md, §3).
 *
 * Без входа: человек открывает ссылку там, где у него почта. Токен — во
 * фрагменте адреса, как у подтверждения почты: до сервера он доезжает только
 * телом запроса и в журналы веб-сервера не попадает, а из адресной строки
 * стирается сразу.
 *
 * Отписка — по кнопке, а не при открытии: почтовые сервисы открывают ссылки
 * из писем сами, проверяя их на вирусы, и человек оказался бы отписан, ничего
 * не нажимая.
 */

function takeTokenFromUrl(): string {
  const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  const token = new URLSearchParams(raw).get('token') ?? '';
  if (token) {
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  }
  return token;
}

export function UnsubscribePage() {
  const [token] = useState(takeTokenFromUrl);
  const run = useMutation({ mutationFn: () => unsubscribe(token) });

  if (run.isSuccess) {
    return (
      <AuthLayout title="Вы отписаны" subtitle="Информационных рассылок больше не будет.">
        <Stack gap="md">
          <Alert color="teal" icon={<IconCheck size={17} />} title="Готово">
            Отказ записан. Письма о вашем аккаунте и оплатах — подтверждение почты, восстановление
            пароля, чеки — продолжат приходить: это не рассылка.
          </Alert>
          <Text size="sm" c="var(--text-muted)">
            Передумаете — рассылки включаются в{' '}
            <Anchor component={Link} to="/profile">
              профиле
            </Anchor>
            .
          </Text>
        </Stack>
      </AuthLayout>
    );
  }

  if (!token) {
    return (
      <AuthLayout title="Ссылка не сработала" subtitle="Похоже, она скопировалась не целиком.">
        <Stack gap="md">
          <Alert color="red" icon={<IconAlertCircle size={17} />}>
            В адресе нет ключа. Откройте ссылку «Отписаться» прямо из письма или выключите рассылки в
            профиле. Можно и написать на support@creativitas.ru с пометкой «отказ от рассылок».
          </Alert>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Отписаться от рассылок" subtitle="Академия Креативитас">
      <Stack gap="md">
        <Text size="sm">
          Нажмите кнопку — и мы больше не будем присылать вам информационные и рекламные письма.
        </Text>
        {run.isError ? (
          <Alert color="red" icon={<IconAlertCircle size={17} />} title="Не получилось">
            {toUserMessage(run.error)}
          </Alert>
        ) : null}
        <Button loading={run.isPending} onClick={() => run.mutate()}>
          Отписаться
        </Button>
      </Stack>
    </AuthLayout>
  );
}
